import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Contract, CartItem, ContractStatus } from "@/types";
import { useState, useEffect } from "react";
import { createBookings } from "@/api/bookings";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useContracts } from "@/contexts/ContractContext";
import { useCalendar } from "@/contexts/CalendarContext";

interface CartDialogProps {
  isOpen: boolean;
  onClose: () => void;
  cart: CartItem[];
  contracts: Contract[];
  onConfirm: (contractId: number, bookingsData?: any) => void;
}

export function CartDialog({ isOpen, onClose, cart, contracts, onConfirm }: CartDialogProps) {
  const [selectedContract, setSelectedContract] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { refreshAvailability } = useCalendar();

  useEffect(() => {
    if (!isOpen) {
      setSelectedContract(null);
    }
  }, [isOpen]);

  const availableCart = cart.filter(item =>
    !contracts.some(contract =>
      contract.bookings?.some(booking =>
        booking.unitNumber === item.roomNumber &&
        booking.week === item.week &&
        booking.years.some(year => item.years.includes(year))
      )
    )
  );

  const totalWeeks = availableCart.reduce((acc, item) => acc + item.years.length, 0);

  const handleConfirm = async () => {
    if (selectedContract) {
      setIsLoading(true);
      try {
        const bookingsToCreate = availableCart.map(item => ({
          unitNumber: item.unitNumber || item.roomNumber,
          resort: item.resort,
          week: item.week,
          years: item.years,
          status: 1,
          isCustom: false,
          externalAvailabilityId: item.externalAvailabilityId,
        }));

        const response = await createBookings(parseInt(selectedContract), bookingsToCreate);
        await refreshAvailability();
        onConfirm(parseInt(selectedContract), response);

        toast.success('Bookings created successfully');
        setSelectedContract(null);
        onClose();
      } catch (error) {
        toast.error('Failed to create booking');
        console.error('Error creating bookings:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-background">
        <DialogHeader>
          <DialogTitle>Booking Cart</DialogTitle>
          <DialogDescription>
            Select a client to assign {totalWeeks} week{totalWeeks !== 1 ? 's' : ''} from your cart.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Select
            value={selectedContract || ''}
            onValueChange={(value) => setSelectedContract(value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a client" />
            </SelectTrigger>
            <SelectContent>
              {contracts
                .filter(contract =>
                  contract.status === ContractStatus.New || contract.status === ContractStatus.Pending
                )
                .map((contract) => (
                  <SelectItem key={contract._id} value={contract._id.toString()}>
                    {contract.owner?.firstName} {contract.owner?.lastName} - {contract.owner?.membershipNumber}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground">
            Cart Summary:
            {availableCart.length === 0 ? (
              <div className="ml-2 mt-1 text-destructive">
                No available weeks in cart.
              </div>
            ) : (
              availableCart.map((item, index) => (
                <div key={index} className="ml-2 mt-1">
                  • Unit {item.unitNumber || item.roomNumber} - Week {item.week}: {item.years.join(', ')}
                </div>
              ))
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedContract || availableCart.length === 0 || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Confirm Bookings'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
